import { apiClient } from './client'
import type { EditorJsData, Homework, HomeworkItem } from '@/types'

/**
 * Пункт в запросе: id — только у сохраняемых, иначе пункт создастся заново.
 *
 * Текстовый пункт несёт документ Editor.js, пункт-задание — ссылку на само
 * задание: своего текста у него нет.
 */
export type HomeworkItemInput = {
  id?: number
  type: 'text' | 'exercise'
  task_json?: EditorJsData
  exercise_id?: number
}

type ApiWrapper<T> = {
  message?: string
  data?: T
}

type IndexResponse<T> =
  | T[]
  | {
      data?: T[]
      message?: string
    }

type HomeworkShowResponse = Homework | { data?: Homework }

function isWrappedHomeworkResponse(
  raw: HomeworkShowResponse
): raw is { data?: Homework } {
  return Boolean(raw && typeof raw === 'object' && 'data' in raw)
}

function unwrapIndex<T>(raw: IndexResponse<T>): T[] {
  if (Array.isArray(raw)) return raw
  if (raw?.data && Array.isArray(raw.data)) return raw.data
  return []
}

export const homeworksApi = {
  // For teacher: list by group
  getByGroup: async (groupId: number): Promise<Homework[]> => {
    const response = await apiClient.get<IndexResponse<Homework>>(`/groups/${groupId}/homeworks`)
    return unwrapIndex(response.data)
  },

  // For teacher: list by user (student)
  getByUser: async (userId: number): Promise<Homework[]> => {
    const response = await apiClient.get<IndexResponse<Homework>>(`/users/${userId}/homeworks`)
    return unwrapIndex(response.data)
  },

  getByUserAndId: async (userId: number, homeworkId: number): Promise<Homework> => {
    const response = await apiClient.get<HomeworkShowResponse>(
      `/users/${userId}/homeworks/${homeworkId}`
    )

    if (isWrappedHomeworkResponse(response.data)) {
      if (response.data.data) return response.data.data
      throw new Error('Домашка не найдена')
    }

    return response.data
  },

  /** Копии домашки на учеников урока. Один текстовый пункт — привычная «домашка текстом» */
  createFromLesson: async (
    lessonId: number,
    payload: { items: HomeworkItemInput[]; student_ids?: number[] }
  ): Promise<Homework[]> => {
    const response = await apiClient.post<ApiWrapper<Homework[]>>(`/lessons/${lessonId}/homeworks`, payload)
    // Backend returns `HomeworkResource::collection(collect($homeworks))` => usually wrapped in `data: [...]`.
    if (response.data?.data) return response.data.data
    return []
  },

  /**
   * Переписать состав пунктов. `id` обязателен для всего, что сохраняется:
   * пункт с id сохраняет ответ ученика, пункт без id создаётся заново.
   */
  replaceItems: async (userId: number, homeworkId: number, items: HomeworkItemInput[]): Promise<Homework> => {
    const response = await apiClient.put<ApiWrapper<Homework>>(
      `/users/${userId}/homeworks/${homeworkId}/items`,
      { items }
    )
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message ?? '')
  },

  /**
   * Ответ на пункт. `answer_json: null` вместе с `completed` — отметка
   * «выполнено» для пункта, который ответа не требует.
   */
  answerItem: async (
    userId: number,
    homeworkId: number,
    itemId: number,
    payload: { answer_json: EditorJsData | null; completed?: boolean }
  ): Promise<HomeworkItem> => {
    const response = await apiClient.patch<ApiWrapper<HomeworkItem>>(
      `/users/${userId}/homeworks/${homeworkId}/items/${itemId}/answer`,
      payload
    )
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка сохранения ответа')
  },

  /** Сдать домашку целиком — отдельный акт, а не следствие ответа на пункты */
  submit: async (userId: number, homeworkId: number): Promise<Homework> => {
    const response = await apiClient.patch<ApiWrapper<Homework>>(
      `/users/${userId}/homeworks/${homeworkId}/submit`,
      {}
    )
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message ?? '')
  },

  checkByUser: async (
    userId: number,
    homeworkId: number,
    payload: { action: 'approve' } | { action: 'revision'; comment: string } = { action: 'approve' }
  ): Promise<Homework> => {
    const response = await apiClient.patch<ApiWrapper<Homework>>(
      `/users/${userId}/homeworks/${homeworkId}/check`,
      payload
    )
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка проверки домашнего задания')
  },
}

